import {
  Avatar,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PaymentIcon from "@mui/icons-material/Payment";
import BoltIcon from "@mui/icons-material/Bolt";
import CampaignIcon from "@mui/icons-material/Campaign";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNavigate } from "react-router-dom";
import { IApplicationState } from "@/store-redux";
import { closeDrawer, setNotifications, appendNotifications } from "@/store-redux/slices/notificationSlice";
import {
  useGetNotifications,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
  useDeleteNotification,
} from "@/services-react-query/queries/notification.query";
import { INotification, NotificationType } from "@/common/types/notification";
import moment from "moment";
import "moment/dist/locale/vi";

moment.locale("vi");

const TYPE_META: Record<NotificationType, { icon: React.ReactNode; color: string; bgColor: string }> = {
  [NotificationType.ASSIGNMENT_NEW]: {
    icon: <AssignmentIcon sx={{ fontSize: 18 }} />,
    color: "#6366F1",
    bgColor: "rgba(99,102,241,0.12)",
  },
  [NotificationType.ASSIGNMENT_SUBMITTED]: {
    icon: <AssignmentIcon sx={{ fontSize: 18 }} />,
    color: "#22C55E",
    bgColor: "rgba(34,197,94,0.12)",
  },
  [NotificationType.ASSIGNMENT_TASK_SCORED]: {
    icon: <AssignmentIcon sx={{ fontSize: 18 }} />,
    color: "#10B981",
    bgColor: "rgba(16,185,129,0.12)",
  },
  [NotificationType.ASSIGNMENT_CLOSED]: {
    icon: <AssignmentIcon sx={{ fontSize: 18 }} />,
    color: "#F59E0B",
    bgColor: "rgba(245,158,11,0.12)",
  },
  [NotificationType.ASSIGNMENT_DUE_SOON]: {
    icon: <AssignmentIcon sx={{ fontSize: 18 }} />,
    color: "#EF4444",
    bgColor: "rgba(239,68,68,0.12)",
  },
  [NotificationType.TEACHER_FEEDBACK]: {
    icon: <AssignmentIcon sx={{ fontSize: 18 }} />,
    color: "#8B5CF6",
    bgColor: "rgba(139,92,246,0.12)",
  },
  [NotificationType.ENERGY_LOW]: {
    icon: <BoltIcon sx={{ fontSize: 18 }} />,
    color: "#F59E0B",
    bgColor: "rgba(245,158,11,0.12)",
  },
  [NotificationType.ENERGY_REFILLED]: {
    icon: <BoltIcon sx={{ fontSize: 18 }} />,
    color: "#22C55E",
    bgColor: "rgba(34,197,94,0.12)",
  },
  [NotificationType.PAYMENT_SUCCESS]: {
    icon: <PaymentIcon sx={{ fontSize: 18 }} />,
    color: "#22C55E",
    bgColor: "rgba(34,197,94,0.12)",
  },
  [NotificationType.PAYMENT_FAILED]: {
    icon: <PaymentIcon sx={{ fontSize: 18 }} />,
    color: "#EF4444",
    bgColor: "rgba(239,68,68,0.12)",
  },
  [NotificationType.SUBSCRIPTION_EXPIRING]: {
    icon: <SubscriptionsIcon sx={{ fontSize: 18 }} />,
    color: "#F59E0B",
    bgColor: "rgba(245,158,11,0.12)",
  },
  [NotificationType.SUBSCRIPTION_EXPIRED]: {
    icon: <SubscriptionsIcon sx={{ fontSize: 18 }} />,
    color: "#EF4444",
    bgColor: "rgba(239,68,68,0.12)",
  },
  [NotificationType.SYSTEM_ANNOUNCEMENT]: {
    icon: <CampaignIcon sx={{ fontSize: 18 }} />,
    color: "#6366F1",
    bgColor: "rgba(99,102,241,0.12)",
  },
};

const getNotificationLink = (notif: INotification): string | null => {
  const { type, data } = notif;
  const assignmentId = data?.assignmentId;

  switch (type) {
    case NotificationType.ASSIGNMENT_NEW:
    case NotificationType.ASSIGNMENT_TASK_SCORED:
    case NotificationType.ASSIGNMENT_CLOSED:
    case NotificationType.ASSIGNMENT_DUE_SOON:
    case NotificationType.TEACHER_FEEDBACK:
      return assignmentId ? `/student/assignments/${assignmentId}` : "/student/assignments";

    case NotificationType.ASSIGNMENT_SUBMITTED:
      return "/teacher/class";

    case NotificationType.ENERGY_LOW:
    case NotificationType.ENERGY_REFILLED:
      return "/account";

    case NotificationType.PAYMENT_SUCCESS:
    case NotificationType.PAYMENT_FAILED:
    case NotificationType.SUBSCRIPTION_EXPIRING:
    case NotificationType.SUBSCRIPTION_EXPIRED:
      return "/upgrade";

    default:
      return null;
  }
};

const NotificationItem = ({
  notif,
  onRead,
  onDelete,
  onNavigate,
}: {
  notif: INotification;
  onRead: (id: string) => void;
  onDelete: (id: string) => void;
  onNavigate: (link: string) => void;
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const meta = TYPE_META[notif.type];
  const link = getNotificationLink(notif);

  const handleClick = () => {
    if (!notif.isRead) {
      onRead(notif._id);
    }
    if (link) {
      onNavigate(link);
    }
  };

  return (
    <Box
      id={`notification-item-${notif._id}`}
      onClick={handleClick}
      sx={{
        display: "flex",
        gap: 1.5,
        px: 2,
        py: 1.5,
        cursor: link ? "pointer" : "default",
        bgcolor: notif.isRead ? "transparent" : isDark ? "rgba(99,102,241,0.06)" : "rgba(99,102,241,0.04)",
        borderLeft: notif.isRead ? "3px solid transparent" : "3px solid #6366F1",
        transition: "background 0.2s ease",
        "&:hover": {
          bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(99,102,241,0.06)",
        },
      }}
    >
      {/* Icon */}
      <Avatar
        sx={{
          width: 36,
          height: 36,
          bgcolor: meta?.bgColor ?? "rgba(99,102,241,0.12)",
          color: meta?.color ?? "#6366F1",
          flexShrink: 0,
          mt: 0.25,
        }}
      >
        {meta?.icon ?? <NotificationsNoneIcon sx={{ fontSize: 18 }} />}
      </Avatar>

      {/* Content */}
      <Box flex={1} minWidth={0}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: notif.isRead ? 500 : 700,
              color: isDark ? "#F1F5F9" : "#1E293B",
              lineHeight: 1.4,
            }}
          >
            {notif.title}
          </Typography>
          {!notif.isRead && (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: "#6366F1",
                flexShrink: 0,
                mt: 0.5,
              }}
            />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4, mt: 0.25 }}>
          {notif.body}
        </Typography>
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
          <Typography variant="caption" sx={{ color: "#94A3B8", fontSize: "11px" }}>
            {moment(notif.createdAt).fromNow()} • {moment(notif.createdAt).format("DD/MM/YYYY HH:mm")}
          </Typography>
          <Tooltip title="Xoá">
            <IconButton
              id={`notification-delete-${notif._id}`}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(notif._id);
              }}
              sx={{
                opacity: 0,
                ".MuiBox-root:hover &": { opacity: 1 },
                color: "text.secondary",
                "&:hover": { color: "#EF4444" },
                p: 0.25,
                transition: "opacity 0.15s, color 0.15s",
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

const EmptyState = ({ label }: { label: string }) => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    py={8}
    px={3}
    textAlign="center"
  >
    <Box
      sx={{
        width: 64,
        height: 64,
        borderRadius: "50%",
        bgcolor: "rgba(99,102,241,0.08)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mb: 2,
      }}
    >
      <NotificationsNoneIcon sx={{ fontSize: 30, color: "#6366F1" }} />
    </Box>
    <Typography variant="body2" fontWeight={700} color="text.primary">
      Không có thông báo
    </Typography>
    <Typography variant="caption" color="text.secondary" mt={0.5}>
      {label}
    </Typography>
  </Box>
);

export const NotificationCenter = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { notifications, unreadCount, isDrawerOpen } = useSelector((state: IApplicationState) => state.notification);

  const [tab, setTab] = useState<0 | 1>(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const { data, isFetching } = useGetNotifications(page);

  useEffect(() => {
    if (data) {
      if (page === 1) {
        dispatch(
          setNotifications({
            notifications: data.items,
            total: data.total,
            unreadCount: data.unreadCount,
          })
        );
      } else {
        dispatch(
          appendNotifications({
            notifications: data.items,
            unreadCount: data.unreadCount,
          })
        );
      }
      setHasMore(notifications.length + data.items.length < data.total);
    }
  }, [data]);

  const fetchMoreData = () => {
    if (!isFetching && hasMore) {
      setPage((prev) => prev + 1);
    }
  };
  const { mutate: markRead } = useMarkNotificationAsRead();
  const { mutate: markAllRead } = useMarkAllNotificationsAsRead();
  const { mutate: deleteNotif } = useDeleteNotification();

  const displayed = tab === 0 ? notifications : notifications.filter((n) => !n.isRead);

  return (
    <Drawer
      id="notification-center-drawer"
      anchor="right"
      open={isDrawerOpen}
      onClose={() => dispatch(closeDrawer())}
      PaperProps={{
        sx: {
          width: { xs: "100vw", sm: 400 },
          bgcolor: isDark ? "#0F172A" : "#FFFFFF",
          backgroundImage: "none",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box
        px={2}
        py={1.5}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6" fontWeight={800} fontSize="17px">
            Thông báo
          </Typography>
          {unreadCount > 0 && (
            <Chip
              label={unreadCount}
              size="small"
              sx={{
                height: 20,
                fontSize: "11px",
                fontWeight: 800,
                bgcolor: "rgba(99,102,241,0.12)",
                color: "#6366F1",
                "& .MuiChip-label": { px: 1 },
              }}
            />
          )}
        </Box>
        <Stack direction="row" spacing={0.5}>
          {unreadCount > 0 && (
            <Tooltip title="Đánh dấu tất cả đã đọc">
              <IconButton
                id="notification-mark-all-btn"
                size="small"
                onClick={() => markAllRead()}
                sx={{ color: "#6366F1", "&:hover": { bgcolor: "rgba(99,102,241,0.08)" } }}
              >
                <DoneAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Đóng">
            <IconButton
              id="notification-close-btn"
              size="small"
              onClick={() => dispatch(closeDrawer())}
              sx={{ color: "text.secondary" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="fullWidth"
        sx={{
          minHeight: 40,
          borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"}`,
          "& .MuiTab-root": { minHeight: 40, fontWeight: 700, fontSize: "13px" },
          "& .MuiTabs-indicator": {
            background: "linear-gradient(90deg, #6366F1 0%, #A855F7 100%)",
          },
        }}
      >
        <Tab id="notif-tab-all" label="Tất cả" />
        <Tab id="notif-tab-unread" label={`Chưa đọc ${unreadCount > 0 ? `(${unreadCount})` : ""}`} />
      </Tabs>

      {/* Loading indicator */}
      {isFetching && (
        <Box display="flex" justifyContent="center" py={1}>
          <CircularProgress size={16} sx={{ color: "#6366F1" }} />
        </Box>
      )}

      {/* Notification list */}
      <Box flex={1} overflow="auto" id="scrollable-drawer">
        {displayed.length === 0 ? (
          isFetching ? null : (
            <EmptyState label={tab === 0 ? "Bạn đã xem hết thông báo! 🎉" : "Không có thông báo chưa đọc."} />
          )
        ) : (
          <InfiniteScroll
            dataLength={displayed.length}
            next={fetchMoreData}
            hasMore={hasMore}
            loader={
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={16} sx={{ color: "#6366F1" }} />
              </Box>
            }
            scrollableTarget="scrollable-drawer"
          >
            {displayed.map((notif, idx) => (
              <Box key={notif._id}>
                <NotificationItem
                  notif={notif}
                  onRead={(id) => markRead(id)}
                  onDelete={(id) => deleteNotif(id)}
                  onNavigate={(link) => {
                    navigate(link);
                    dispatch(closeDrawer());
                  }}
                />
                {idx < displayed.length - 1 && (
                  <Divider
                    sx={{
                      borderColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                      mx: 2,
                    }}
                  />
                )}
              </Box>
            ))}
          </InfiniteScroll>
        )}
      </Box>
    </Drawer>
  );
};
